import { supabase } from './supabaseClient';

const BOT_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
const BOT_USERNAME = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;

export async function getMessages(studentId, limit = 100) {
  const { data, error } = await supabase
    .from('chat_messages')
    .select('id, sender_role, sender_name, text, source, created_at')
    .eq('student_id', studentId)
    .order('created_at', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function sendMessage({ studentId, senderRole, senderName, text }) {
  const { data, error } = await supabase
    .from('chat_messages')
    .insert({ student_id: studentId, sender_role: senderRole, sender_name: senderName, text, source: 'app' })
    .select()
    .single();
  if (error) throw error;

  forwardToTelegram(studentId, text, senderRole, senderName).catch(() => {});

  // Soft-realtime nudge for anything showing an unread badge (sidebar,
  // students list, student profile) — same pattern as the existing
  // 'homeworkUpdated' window event used throughout the app.
  try {
    window.dispatchEvent(new CustomEvent('chatMessageSent', { detail: { studentId, senderRole } }));
  } catch (_) {}

  return data;
}

export function subscribeMessages(studentId, onInsert) {
  const channel = supabase
    .channel(`chat-${studentId}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'chat_messages',
      filter: `student_id=eq.${studentId}`,
    }, (payload) => onInsert(payload.new))
    .subscribe();

  return () => supabase.removeChannel(channel);
}

export async function getTelegramChatId(studentId, role = 'teacher') {
  const { data } = await supabase
    .from('telegram_links')
    .select('telegram_chat_id')
    .eq('student_id', studentId)
    .eq('role', role)
    .single();
  return data?.telegram_chat_id || null;
}

export function getTelegramConnectUrl(studentId, isStudent = false, userName = '') {
  if (!BOT_USERNAME || !studentId) return null;
  const safeName = userName
    ? '__' + userName.replace(/[^A-Za-z0-9]/g, '-').slice(0, 20)
    : '';
  const param = isStudent ? `s_${studentId}${safeName}` : `${studentId}${safeName}`;
  return `https://t.me/${BOT_USERNAME}?start=${param}`;
}

export async function forwardToTelegram(studentId, text, senderRole, senderName) {
  if (!BOT_TOKEN) {
    console.warn('[telegram] BOT_TOKEN missing, skipping forward');
    return;
  }
  const targetRole = senderRole === 'teacher' ? 'student' : 'teacher';
  const chatId = await getTelegramChatId(studentId, targetRole);
  if (!chatId) {
    console.warn('[telegram] no chat link found', { studentId, lookupRole: targetRole, senderRole });
    return;
  }

  const label = senderRole === 'student'
    ? `👤 ${senderName || 'Student'}`
    : `👨‍🏫 ${senderName || 'Teacher'}`;

  let res;
  try {
    res = await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chat_id: chatId, text: `${label}:\n${text}` }),
    });
  } catch (e) {
    console.warn('[telegram] fetch failed', e?.message);
    return;
  }

  const json = await res.json().catch(() => ({}));
  if (!json.ok) {
    console.warn('[telegram] sendMessage rejected', { studentId, targetRole, chatId, description: json.description });
    return;
  }
  if (json.result?.message_id) {
    // Save mapping so Edge Function can route replies without visible marker
    supabase.from('telegram_message_map').insert({
      message_id: json.result.message_id,
      chat_id: chatId,
      student_id: studentId,
    }).catch(() => {});
  }
}
